import { Calendar, Home, Search, User, LogIn, LogOut, Globe, Check } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePlatform } from "@/hooks/usePlatform";
import { useLanguage } from "@/contexts/LanguageContext";
import NotificationIcon from "./NotificationIcon";
import ChatIcon from "./ChatIcon";
import logoRacketPlayGrid from "@/assets/logo-racketplaygrid.jpg";

interface PlayerNavProps {
    activeTab: string;
    setActiveTab: (tab: string) => void;
    notificationCount?: number;
    chatCount?: number;
    onNotificationClick?: () => void;
    onChatClick?: () => void;
    onTabClick?: (tab: string) => void;
    hideNotificationBadge?: boolean;
}

const PlayerNav = ({
                       activeTab,
                       setActiveTab,
                       notificationCount = 0,
                       chatCount = 0,
                       onNotificationClick = () => {},
                       onChatClick = () => {},
                       onTabClick,
                       hideNotificationBadge
                   }: PlayerNavProps) => {
    const { user, signOut } = useAuth();
    const { isIOS, isNative, isAndroid } = usePlatform();
    const { language, setLanguage, t } = useLanguage();

    const navItems = [
        { id: "dashboard", label: t('nav.home'), icon: Home },
        { id: "events", label: t('nav.explore'), icon: Search },
        { id: "my-events", label: t('nav.myEvents'), icon: Calendar },
        { id: "profile", label: t('nav.profile'), icon: User },
    ];

    const handleTabClick = (tabId: string) => {
        if (onTabClick) {
            onTabClick(tabId);
        } else {
            setActiveTab(tabId);
        }
    };

    return (
        <>
            {/* Top Header - Con safe area para iOS */}
            <header
                className="bg-white/90 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50"
                style={{
                    paddingTop: (isIOS || isAndroid) && isNative ? '44px' : '0px'
                }}
            >
                <div className="container mx-auto px-2 md:px-4">
                    <div className="flex items-center justify-between h-12 md:h-16">
                        <div className="flex items-center space-x-1.5 md:space-x-2">
                            <img
                                src={logoRacketPlayGrid}
                                alt="Racket PlayGrid"
                                className="w-6 h-6 md:w-8 md:h-8 rounded-lg object-cover"
                            />
                            <span className="font-bold text-base md:text-lg text-gray-900">
                Racket PlayGrid
              </span>
                        </div>

                        {/* Communication Icons - Notifications and Chats */}
                        <div className="flex items-center space-x-1 md:space-x-2">
                            {/* Language Selector */}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-gray-600 hover:text-gray-900 p-1.5 md:p-2 flex items-center gap-1"
                                    >
                                        <Globe className="h-3.5 w-3.5 md:h-4 md:w-4" />
                                        <span className="text-xs font-semibold">{language.toUpperCase()}</span>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => setLanguage('es')} className="flex items-center justify-between">
                                        Español
                                        {language === 'es' && <Check className="h-4 w-4 ml-2" />}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setLanguage('en')} className="flex items-center justify-between">
                                        English
                                        {language === 'en' && <Check className="h-4 w-4 ml-2" />}
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>

                            {user ? (
                                <>
                                    <NotificationIcon onClick={onNotificationClick} hideBadge={hideNotificationBadge} />
                                    <ChatIcon onClick={onChatClick} />
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={signOut}
                                        className="text-gray-600 hover:text-red-600 p-1.5 md:p-2"
                                    >
                                        <LogOut className="h-3.5 w-3.5 md:h-4 md:w-4" />
                                    </Button>
                                </>
                            ) : (
                                <Link to="/auth">
                                    <Button
                                        variant="default"
                                        size="sm"
                                        className="bg-gradient-to-r from-[#3D6B4F] to-[#4A7D5E] hover:from-[#345D44] hover:to-[#3D6B4F] text-white font-medium shadow-lg hover:shadow-xl transition-all duration-300 ease-in-out transform hover:scale-105 rounded-full text-xs md:text-sm px-4 md:px-6 py-2 border-0"
                                    >
                                        <LogIn className="h-3.5 w-3.5 md:h-4 md:w-4 mr-1.5 md:mr-2" />
                                        <span className="hidden sm:inline">{t('auth.signIn')}</span>
                                        <span className="sm:hidden">{t('auth.login')}</span>
                                    </Button>
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Bottom Navigation - Con safe area para iOS */}
            <nav
                className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-gray-200 z-50"
                style={{
                    paddingBottom: (isIOS || isAndroid) && isNative ? '20px' : '0px'
                }}
            >
                <div className="grid grid-cols-4 h-14 md:h-16">
                    {navItems.map((item) => {
                        const Icon = item.icon;
                        return (
                            <Button
                                key={item.id}
                                variant="ghost"
                                onClick={() => handleTabClick(item.id)}
                                className={`flex flex-col items-center justify-center h-full rounded-none space-y-0.5 md:space-y-1 ${
                                    activeTab === item.id
                                        ? "text-[#1b3521]"
                                        : "text-[#1b3521] hover:text-[#1b3521] hover:bg-gray-50"
                                }`}
                                style={activeTab === item.id ? { backgroundColor: 'rgba(231, 209, 182, 0.58)' } : undefined}
                            >
                                <Icon className="h-4 w-4 md:h-5 md:w-5" />
                                <span className="text-[10px] md:text-xs font-medium">{item.label}</span>
                            </Button>
                        );
                    })}
                </div>
            </nav>
        </>
    );
};

export default PlayerNav;
